import { ISymbolDetail, marketAPIController } from '@aqx-core/common/api/market';
import {
  EAlertFrequency,
  EAlertStatus,
  ICreatePriceAlertParams,
  IInitSymbolPriceAlerts,
  IPriceAlert,
  IPriceAlertTriggeredData,
  IUpdatePriceAlertParams,
  TPriceAlertSymbolDetail,
  priceAlertAPIController
} from '@aqx-core/common/api/price-alert';
import { EPriceAlertTab, MAX_ALERTS_FOR_ALL_SYMBOLS } from '@aqx-core/common/constants/price-alert';
import { i18n } from '@aqx-core/common/i18n';
import { NETWORK_ERROR_MESSAGE, mapResponseCodeToMessage } from '@aqx-core/common/request';
import {
  ISymbolTopicLivePrices,
  SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES,
  symbolTopicSubscription
} from '@aqx-core/common/socket/subscriptions';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { getAlertConditionLabel, getAlertFrequencyLabel } from '@aqx-core/common/utils/price-alert';

import { EUISnackBarMessageType, snackbarController } from '../snackbar';
import { tradeAnalysisService } from '../trade/analysis';
import { equityChartViewController } from '../trade/analysis/equity-chart/controller';
import { priceAlertService } from './service';
import {
  ILivePricesMap,
  IPriceAlertApiError,
  IPriceAlertMap,
  IPriceAlertSymbolDetailMap,
  ISymbolDisabledMap,
  ISymbolExpandedMap,
  ISymbolPriceAlertsMap
} from './types';

interface ICreatePriceAlertParamsObj {
  values: ICreatePriceAlertParams;
  formSymbol?: string;
  isMobileWeb?: boolean;
  onSuccessCallback?: () => void;
  onError?: (error: IPriceAlertApiError) => boolean;
  disableRequery?: boolean;
  onRequerySuccess?: (symbol: string) => void;
}

interface IUpdatePriceAlertParamsObj extends Omit<ICreatePriceAlertParamsObj, 'values'> {
  newAlert: IUpdatePriceAlertParams;
}

interface IDeleteAlertIdsParamsObj {
  alertIds: string[];
  isMobileWeb?: boolean;
  onSuccessCallback?: () => void;
  onErrorCallback?: () => void;
  onDeleteRequerySuccess?: (alertIds: string[]) => void;
  isDesktop?: boolean;
}

export interface IHandlePriceAlertToggleParams {
  priceAlert: IPriceAlert;
  onSuccess?: (alert: IPriceAlert) => void;
  isDesktop?: boolean;
}

class PriceAlertViewController {
  private currentSymbolSubscriptionKey = new Set<string>();
  private isInitialized = false;

  public init = () => {
    if (this.isInitialized) {
      return;
    }

    if (!priceAlertService.context.currentSymbol) {
      const lastSelectedSymbol = storage.getItem<string>(EStorageKey.LAST_SELECTED_SYMBOL);
      if (lastSelectedSymbol) {
        priceAlertService.setCurrentSymbol(lastSelectedSymbol);
      }
    }

    this.isInitialized = true;
    this.getAllPriceAlerts();
    this.fetchAlertConfig();
  };

  private fetchCurrentSymbolDetailIfMissing = async () => {
    const currentSymbol = priceAlertService.context.currentSymbol;
    if (!currentSymbol) {
      return;
    }

    // If symbol detail is already available (e.g. set via setSingleSymbolDetail before navigation), skip
    if (priceAlertService.context.symbolDetailsMap[currentSymbol]) {
      return;
    }

    await marketAPIController.getSymbolDetail(
      { symbol: currentSymbol },
      {
        onSuccess: (symbolDetail) => {
          if (symbolDetail) {
            this.setSingleSymbolDetail(symbolDetail as TPriceAlertSymbolDetail);
          }
        },
        onError: () => {}
      }
    );
  };

  private fetchAlertConfig = () => {
    priceAlertService.setAlertConfigLoading(true);
    priceAlertAPIController.getAlertConfig({
      onSuccess: (config) => {
        if (config) {
          priceAlertService.setAlertConfig(config);
        }
      },
      onError: () => {},
      onFinally: () => {
        priceAlertService.setAlertConfigLoading(false);
      }
    });
  };

  private getAllPriceAlerts = async (shouldLoading: boolean = true, onSuccess?: () => void) => {
    if (shouldLoading) {
      priceAlertService.setPriceAlertsLoading(true);
    }

    await priceAlertAPIController.getPriceAlerts(
      {
        cursorId: 0,
        limit: MAX_ALERTS_FOR_ALL_SYMBOLS
      },
      {
        onSuccess: (priceAlertsArr) => {
          if (priceAlertsArr) {
            const symbolDetailsMap: IPriceAlertSymbolDetailMap = {};
            const symbolDisabledMap: ISymbolDisabledMap = {};
            const symbolExpandedMap: ISymbolExpandedMap = {};

            const currentSymbolExpandedObj = priceAlertService.context.symbolExpandedMap ?? {};
            const currentSymbolDetail = tradeAnalysisService.context.symbolDetail;

            const priceAlertsMap = priceAlertsArr.reduce(
              (prev: ISymbolPriceAlertsMap, priceAlertObj: IInitSymbolPriceAlerts) => {
                const { alertList, symbol } = priceAlertObj;

                if (alertList.length === 0) {
                  return prev;
                }

                const alerts: IPriceAlertMap = {};
                let detail: TPriceAlertSymbolDetail | undefined;
                let noOfDisabledAlerts: number = 0;
                alertList.forEach((priceAlert, index) => {
                  const {
                    bid,
                    decimal,
                    description,
                    open,
                    pointStep,
                    prevClose,
                    symbol,
                    tickRulesGroup,
                    type,
                    status
                  } = priceAlert;

                  // If currentSymbolDetail symbolId matches priceAlert symbolId, use currentSymbolDetail to set bid price
                  // Only do this for 1st price alert
                  if (currentSymbolDetail && currentSymbolDetail.symbol === priceAlert.symbol && index === 0) {
                    detail = currentSymbolDetail as TPriceAlertSymbolDetail;
                  } else if (index === 0) {
                    detail = {
                      bid,
                      decimal,
                      description,
                      open,
                      pointStep,
                      prevClose,
                      symbol,
                      tickRulesGroup,
                      type
                    };
                  }
                  // Count total number of disabled alerts
                  // If noOfDisabledAlerts === alertList.length, set symbolDisabled to true
                  if (status === EAlertStatus.DISABLED) {
                    noOfDisabledAlerts += 1;
                  }
                  alerts[priceAlert.alertId] = priceAlert;
                });

                if (detail) {
                  symbolDetailsMap[symbol] = detail;
                }

                symbolDisabledMap[symbol] = noOfDisabledAlerts === alertList.length;

                symbolExpandedMap[symbol] = currentSymbolExpandedObj[symbol] ?? false;

                if (Object.keys(alerts).length > 0) {
                  prev[symbol] = {
                    ...priceAlertObj,
                    alerts
                  };
                }
                return prev;
              },
              {}
            );
            priceAlertService.setPriceAlertsMap(priceAlertsMap);
            priceAlertService.updateSymbolDetailsMap(symbolDetailsMap);
            priceAlertService.updateSymbolDisabledMap(symbolDisabledMap);
            priceAlertService.updateSymbolExpandedMap(symbolExpandedMap);

            this.updateLivePrices(symbolDetailsMap);

            onSuccess?.();
          }
        },
        onError: ({ code, message }) => {
          // Show error alert if message is not cancelled (i.e. when query cancelled due to multiple queries)
          if (message !== 'canceled') {
            snackbarController.showAlert({
              message: mapResponseCodeToMessage(code, message),
              messageType: EUISnackBarMessageType.ERROR
            });
          }
        },
        onFinally: () => {
          this.fetchCurrentSymbolDetailIfMissing().finally(() => {
            priceAlertService.setPriceAlertsLoading(false);
          });
        }
      }
    );
  };

  public refreshPriceAlerts = async () => {
    await this.getAllPriceAlerts(false);
  };

  public fetchPriceAlertsBySymbol = async (symbol: string) => {
    await priceAlertAPIController.getPriceAlertsBySymbol(symbol, {
      onSuccess: (result) => {
        if (result) {
          const currentMap = priceAlertService.context.priceAlertsMap;
          const alerts: IPriceAlertMap = {};
          for (const alert of result.alertList) {
            alerts[alert.alertId] = alert;
          }
          if (Object.keys(alerts).length > 0) {
            priceAlertService.setPriceAlertsMap({
              ...currentMap,
              [result.symbol]: { ...result, alerts }
            });
          } else {
            // Remove symbol from map if no alerts exist
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [result.symbol]: _, ...rest } = currentMap;
            priceAlertService.setPriceAlertsMap(rest);
          }
        }
      },
      onError: () => {}
    });
  };

  public resetPriceAlerts = () => {
    this.isInitialized = false;

    priceAlertService.clearCurrentSymbol();

    priceAlertService.setShowBulkEdit(false);
    priceAlertService.clearEditAlertId();
    priceAlertService.clearAllSelectedAlerts();
    priceAlertService.setActiveTab(EPriceAlertTab.CURRENT_SYMBOL);

    priceAlertService.clearPriceAlertsMap();
    priceAlertService.clearSymbolDetailsMap();
    priceAlertService.clearSymbolDisabledMap();
    priceAlertService.clearSymbolExpandedMap();
    priceAlertService.clearLivePricesMap();
  };

  private updateLivePrices = (symbolDetailsMap: IPriceAlertSymbolDetailMap) => {
    const livePricesMap: ILivePricesMap = Object.values(symbolDetailsMap).reduce(
      (prev: ILivePricesMap, symbolDetail: TPriceAlertSymbolDetail) => {
        const { bid, open, prevClose, symbol } = symbolDetail;
        prev[symbol] = { bid, open, prevClose };
        return prev;
      },
      {}
    );
    priceAlertService.updateLivePricesMap(livePricesMap);
  };

  public setCurrentSymbol = (symbol: string) => {
    priceAlertService.setCurrentSymbol(symbol);
  };

  public setSingleSymbolDetail = (symbolDetail?: TPriceAlertSymbolDetail) => {
    if (!symbolDetail) {
      return;
    }

    const incomingSymbolDetailMap = { [symbolDetail.symbol]: symbolDetail };
    priceAlertService.updateSymbolDetailsMap(incomingSymbolDetailMap);

    this.updateLivePrices(incomingSymbolDetailMap);
  };

  public subscribeSymbolTopic = (symbol: string) => {
    if (!this.isSymbolSubscribed(symbol)) {
      symbolTopicSubscription.subscribe(symbol, SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES, this.updateSymbolPrice);

      this.currentSymbolSubscriptionKey.add(symbol);
    }
  };

  public unsubscribeSymbolTopic = (symbol: string) => {
    if (this.isSymbolSubscribed(symbol)) {
      symbolTopicSubscription.unsubscribe(symbol, SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES, this.updateSymbolPrice);

      this.currentSymbolSubscriptionKey.delete(symbol);
    }
  };

  public isSymbolSubscribed = (symbol: string) => {
    return this.currentSymbolSubscriptionKey.has(symbol);
  };

  private updateSymbolPrice = (updatedSymbolId: string, livePrices: ISymbolTopicLivePrices) => {
    priceAlertService.updateBidPriceOnLivePrices(livePrices.liveSell, updatedSymbolId);
  };

  public setShowBulkEdit = (showBulkEdit: boolean) => {
    priceAlertService.setShowBulkEdit(showBulkEdit);
  };

  public selectAlertIds = (alertIds: string[]) => {
    priceAlertService.addAlertIdsToSelected(alertIds);
  };

  public deselectAlertIds = (alertIds: string[]) => {
    priceAlertService.removeAlertIdsFromSelected(alertIds);
  };

  public selectAllAlertIds = (alertIds: string[]) => {
    priceAlertService.setAllSelectedAlerts(alertIds);
  };

  public deselectAllAlertIds = () => {
    priceAlertService.clearAllSelectedAlerts();
  };

  public handlePriceAlertToggle = (params: IHandlePriceAlertToggleParams) => {
    const { priceAlert, isDesktop = false, onSuccess } = params;
    const updatePriceAlertParams: IUpdatePriceAlertParams = {
      ...priceAlert,
      targetValue: priceAlert.targetPrice,
      toggle: !priceAlert.toggle
    };
    priceAlertAPIController.updatePriceAlert(updatePriceAlertParams, {
      onSuccess: (updatedPriceAlert) => {
        if (updatedPriceAlert) {
          priceAlertService.updatePriceAlert(updatedPriceAlert);

          onSuccess?.(updatedPriceAlert);
        }
      },
      onError: ({ code, message }) => {
        // Show error alert if message is not cancelled (i.e. when query cancelled due to multiple queries)
        if (message !== 'canceled') {
          const showSeparateNetworkError = Boolean(message?.includes(NETWORK_ERROR_MESSAGE)) && isDesktop;
          const alertMessage = showSeparateNetworkError
            ? i18n.t('Network error.')
            : mapResponseCodeToMessage(code, message);
          const description = showSeparateNetworkError ? i18n.t('Please try again later.') : undefined;

          snackbarController.showAlert({
            message: alertMessage,
            messageType: EUISnackBarMessageType.ERROR,
            description
          });
        }
      }
    });
  };

  public createAlert = (params: ICreatePriceAlertParamsObj) => {
    const { formSymbol, isMobileWeb, onError, onRequerySuccess, onSuccessCallback, values } = params;
    if (!formSymbol) {
      return;
    }

    priceAlertService.setSubmitting(true);

    const handleRequerySuccess = () => {
      onRequerySuccess?.(formSymbol);
    };

    priceAlertAPIController.createPriceAlert(
      {
        ...values,
        symbol: formSymbol
      },
      {
        onSuccess: () => {
          onSuccessCallback?.();
          setTimeout(
            () => {
              snackbarController.showAlert({
                message: i18n.t('Price alert has been created.'),
                messageType: EUISnackBarMessageType.SUCCESS
              });
            },
            isMobileWeb ? 100 : 0
          );

          this.getAllPriceAlerts(true, handleRequerySuccess);
        },
        onError: (error) => {
          const shouldReturn = onError ? onError(error) : false;
          if (shouldReturn) {
            return;
          }

          snackbarController.showAlert({
            message: i18n.t('Failed to create price alert.'),
            messageType: EUISnackBarMessageType.ERROR,
            description: i18n.t('Please try again.')
          });
        },
        onFinally: () => {
          priceAlertService.setSubmitting(false);
        }
      }
    );
  };

  public updateAlert = (params: IUpdatePriceAlertParamsObj) => {
    const { formSymbol, isMobileWeb, newAlert, onError, onRequerySuccess, onSuccessCallback } = params;
    if (!formSymbol || !newAlert) {
      return;
    }

    priceAlertService.setSubmitting(true);

    const handleRequerySuccess = () => {
      onRequerySuccess?.(formSymbol);
    };

    priceAlertAPIController.updatePriceAlert(newAlert, {
      onSuccess: () => {
        const { condition, frequencyCondition, targetValue } = newAlert;
        onSuccessCallback?.();
        setTimeout(
          () => {
            snackbarController.showAlert({
              message: i18n.t('Price alert has been updated.'),
              messageType: EUISnackBarMessageType.SUCCESS,
              description: `${formSymbol} ${getAlertConditionLabel(condition)} ${targetValue} | ${getAlertFrequencyLabel(frequencyCondition)}`
            });
          },
          isMobileWeb ? 100 : 0
        );
        priceAlertViewController.clearEditAlertId();

        this.getAllPriceAlerts(true, handleRequerySuccess);
      },
      onError: (error) => {
        const shouldReturn = onError ? onError(error) : false;
        if (shouldReturn) {
          return;
        }

        snackbarController.showAlert({
          message: i18n.t('Failed to update price alert.'),
          messageType: EUISnackBarMessageType.ERROR,
          description: i18n.t('Please try again.')
        });
      },
      onFinally: () => {
        priceAlertService.setSubmitting(false);
      }
    });
  };

  public deleteAlertIds = (props: IDeleteAlertIdsParamsObj) => {
    const {
      alertIds,
      isMobileWeb = false,
      onSuccessCallback,
      onErrorCallback,
      onDeleteRequerySuccess,
      isDesktop = false
    } = props;
    const noOfAlerts = alertIds.length;
    const hasMoreThanOneAlerts = noOfAlerts > 1;
    if (noOfAlerts === 0) {
      return;
    }

    priceAlertAPIController.bulkDeletePriceAlerts(
      {
        ids: alertIds
      },
      {
        onSuccess: async (bulkDeleteResponse) => {
          if (bulkDeleteResponse) {
            const { successList } = bulkDeleteResponse;

            const handleDeleteRequerySuccess = () => {
              onDeleteRequerySuccess?.(successList);
            };

            setTimeout(
              () => {
                snackbarController.showAlert({
                  message:
                    successList.length > 1
                      ? i18n.t('{{noOfAlerts}} Price alerts have been deleted.', { noOfAlerts: successList.length })
                      : i18n.t('Price alert has been deleted.'),
                  messageType: EUISnackBarMessageType.SUCCESS
                });
              },
              isMobileWeb ? 100 : 0
            );

            priceAlertViewController.deselectAllAlertIds();
            priceAlertViewController.setShowBulkEdit(false);
            priceAlertViewController.clearEditAlertId();

            await this.getAllPriceAlerts(false, handleDeleteRequerySuccess);
            onSuccessCallback?.();
          }
        },
        onError: (error) => {
          onErrorCallback?.();

          if (error.message?.includes(NETWORK_ERROR_MESSAGE)) {
            const message = isDesktop ? i18n.t('Network error.') : i18n.t('Network error. Please try again later.');
            const description = isDesktop ? i18n.t('Please try again later.') : undefined;
            snackbarController.showAlert({
              description,
              message,
              messageType: EUISnackBarMessageType.ERROR
            });
            return;
          }

          snackbarController.showAlert({
            message: hasMoreThanOneAlerts
              ? i18n.t('Failed to delete price alerts.')
              : i18n.t('Failed to delete price alert.'),
            messageType: EUISnackBarMessageType.ERROR,
            description: i18n.t('Please try again.')
          });
        }
      }
    );
  };

  public clearEditAlertId = () => {
    priceAlertService.clearEditAlertId();
  };

  public setEditAlertId = (alertId: string) => {
    priceAlertService.setEditAlertId(alertId);
  };

  public setActiveTab = (tab: EPriceAlertTab) => {
    priceAlertService.setActiveTab(tab);
  };

  public showAlertForm = () => {
    priceAlertService.setShowAlertForm(true);
  };

  public handleChangeTab = (value: EPriceAlertTab) => {
    this.setActiveTab(value);

    // Reset All Selected checkbox so that previously selected boxes are not carried over
    this.deselectAllAlertIds();
  };

  public hideAlertForm = () => {
    priceAlertService.setShowAlertForm(false);
    // Clear Edit Alert Id when navigating away from Alert Form
    this.clearEditAlertId();
  };

  public handleEditAlert = (alertId: string, onClickEdit?: () => void) => {
    this.setEditAlertId(alertId);

    // Cancel show bulk edit + deselect all price alert ids
    this.setShowBulkEdit(false);
    this.deselectAllAlertIds();

    onClickEdit?.();
  };

  public goToPriceAlertManagement = (
    symbolDetail: Partial<ISymbolDetail> | undefined,
    onGoToPriceAlert: () => void
  ) => {
    if (!symbolDetail?.symbol) {
      return;
    }
    this.setCurrentSymbol(symbolDetail.symbol);
    this.setSingleSymbolDetail(symbolDetail as TPriceAlertSymbolDetail);
    onGoToPriceAlert();
  };

  public setExpandedForSymbol = (symbol: string, nextExpanded: boolean) => {
    priceAlertService.updateSymbolExpandedMap({ [symbol]: nextExpanded });
  };

  public checkAlertLimitReached = (): boolean => {
    const { alertConfig, currentSymbol, priceAlertsMap } = priceAlertService.context;

    const currentSymbolAlertCount = currentSymbol ? Object.keys(priceAlertsMap[currentSymbol]?.alerts ?? {}).length : 0;

    if (currentSymbolAlertCount >= alertConfig.symbolLimit) {
      return true;
    }

    let totalAlertCount = 0;
    Object.values(priceAlertsMap).forEach(({ alerts }) => {
      totalAlertCount += Object.keys(alerts).length;
    });

    return totalAlertCount >= alertConfig.accountLimit;
  };

  public handlePriceAlertTriggered = (data: IPriceAlertTriggeredData) => {
    const { alertId, frequencyCondition } = data;

    const symbol = this.findAlertSymbolById(alertId);
    if (!symbol) {
      return;
    }

    if (frequencyCondition === EAlertFrequency.ONLY_ONCE) {
      priceAlertService.updateAlertToggle(alertId, symbol, false);
      equityChartViewController.removeSyncPriceAlerts([alertId]);
    }
  };

  private findAlertSymbolById = (alertId: string): string | undefined => {
    const { priceAlertsMap } = priceAlertService.context;
    for (const symbol of Object.keys(priceAlertsMap)) {
      if (priceAlertsMap[symbol].alerts[alertId]) {
        return symbol;
      }
    }
    return undefined;
  };

  public setOnDeleteResetList = (onDeleteResetList: boolean) => {
    priceAlertService.setOnDeleteResetList(onDeleteResetList);
  };
}

export const priceAlertViewController = new PriceAlertViewController();
